import React, { useState, useEffect } from "react";
import { CreditCard, Zap, ChevronDown, ChevronUp } from "lucide-react";
import usePaymentTransactionsByStation from "../../hooks/usePaymentTransactionsByStation";
import { exportToCSV } from "../../utils/exportData";
import { formatINR } from "../../utils/formatINR";

const PaymentTransactionsMobile = ({ stationId = "STATION_001" }) => {
  const { transactions, isLoading, error } = usePaymentTransactionsByStation(stationId);
  const [localTransactions, setLocalTransactions] = useState([]);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState("all");

  const handleExport = () => {
    exportToCSV(transactions, `payment_transactions_${stationId}`);
  };

  useEffect(() => {
    if (transactions) {
      const cleaned = transactions.map((t) => ({
        ...t,
        id: t.id ?? "",
        payment_method_type: t.payment_method_type ?? "",
        amount: t.amount ?? 0,
        gst_amount: t.gst_amount ?? 0,
        final_cost: t.final_cost ?? 0,
        status: t.status ?? "",
        payment_reference_id: t.payment_reference_id?.String ?? "",
        created_at: t.created_at ? new Date(t.created_at).toLocaleString() : "",
      }));
      setLocalTransactions(cleaned);
    }
  }, [transactions]);

  const toggleCardExpansion = (index) => {
    const newExpanded = new Set(expandedCards);
    expandedCards.has(index) ? newExpanded.delete(index) : newExpanded.add(index);
    setExpandedCards(newExpanded);
  };

  const getStatusBadge = (status) => {
    const lower = status.toLowerCase();
    const statusMap = {
      completed: { text: "Completed", color: "green" },
      processed: { text: "Processed", color: "gray" },
      captured: { text: "Captured", color: "red" },
    };
    const s = statusMap[lower] || { text: status, color: "gray" };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${s.color}-50 text-${s.color}-700`}>
        <span className={`w-2 h-2 bg-${s.color}-700 rounded-full mr-1`}></span>
        {s.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b px-4 py-4">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Payment Transactions for Station {stationId}</h1>
        {/* Filter + Export */}
        <div className="flex justify-between items-center mb-4 gap-2">
          <div className="flex gap-2 overflow-x-auto">
            {["all", "completed", "captured", "processed"].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border transition ${
                  filterStatus === status
                    ? "bg-green-200 text-green-900 shadow-sm border-green-300"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            Export
          </button>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Loading State */}
        {isLoading && <div className="py-12 text-center text-gray-600">Loading transactions...</div>}

        {/* Error State */}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-600">{error}</div>}

        {/* Empty State */}
        {!isLoading && !error && localTransactions.filter(t => filterStatus === "all" || t.status.toLowerCase() === filterStatus).length === 0 && (
          <div className="bg-white rounded-lg border p-8 text-center">
            <CreditCard className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your filters to see more results.</p>
          </div>
        )}

        {/* Transactions List */}
        <div className="space-y-4">
          {localTransactions.filter(t => filterStatus === "all" || t.status.toLowerCase() === filterStatus).map((transaction, idx) => (
            <div key={idx} className="bg-white rounded-lg border shadow overflow-hidden">
              {/* Card Header */}
              <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <Zap className="w-4 h-4 text-gray-600" />
                  Transaction #{transaction.id}
                </div>
                {getStatusBadge(transaction.status)}
              </div>

              {/* Card Content */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Amount</p>
                    <p className="text-lg font-semibold text-gray-900">{formatINR(transaction.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Final Cost</p>
                    <p className="text-lg font-semibold text-green-600">{formatINR(transaction.final_cost)}</p>
                  </div>
                </div>

                {/* Expandable Details */}
                <button
                  onClick={() => toggleCardExpansion(idx)}
                  className="w-full flex justify-between items-center py-2 text-sm font-medium border-t border-gray-200 mt-2 text-gray-700"
                >
                  {expandedCards.has(idx) ? "Hide Details" : "View Details"}
                  {expandedCards.has(idx) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {expandedCards.has(idx) && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    {[
                      { label: "Payment Method", value: transaction.payment_method_type },
                      { label: "GST", value: transaction.gst_amount },
                      { label: "Payment Ref", value: transaction.payment_reference_id },
                      { label: "Created At", value: transaction.created_at },
                    ].map(item => item.value && (
                      <React.Fragment key={item.label}>
                        <span className="text-gray-500">{item.label}</span>
                        <span className="font-semibold text-gray-900">{item.value}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PaymentTransactionsMobile;
